/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CustomTabs
 *
 * @file CustomTabs.jsx
 * @author Ujjwal Sana
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import TabPanel from './TabPanel';
import mixins from '../../assets/styles/mixins.scss';

const TabsWrapper = styled(Box)({
  '&&': {
    borderRadius: '8px',
    padding: '0px',
    margin: '0px',
    color: 'black',
    paddingLeft: 0,
    marginLeft: 0,
    paddingRight: 0,
    marginRight: 0,
    backgroundColor: 'transparent'
  }
});

const TabBarRow = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  }
});

const LeadingAction = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    paddingLeft: '16px'
  }
});

const customTabRootStyles = {
  maxWidth: 'none',
  minHeight: 'unset !important',
  textTransform: 'none',
  margin: '0 !important',
  fontSize: '16px',
  fontWeight: 500,
  color: '#171717',
  backgroundColor: 'transparent',
  position: 'relative',
  borderRadius: '0px',
  opacity: '1 !important',
  fontFamily: 'Plus Jakarta Sans',
  lineHeight: '16px',
  padding: '8px 16px 12px 16px',
  border: 'none',
  '&:not(.Mui-selected):hover': {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    background: `${mixins.tabHoverColor}`
  },
  '&.Mui-selected': {
    fontWeight: '500 !important',
    backgroundColor: 'transparent',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '4px',
      backgroundColor: '#C1BFFF',
      borderRadius: '8px'
    },
    // focus-visible treatment for a tab that is selected and focused
    '&.Mui-focusVisible::after': {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      background: 'none',
      border: `2px solid ${mixins.tabFocusVisibleColor}`,
      width: 'calc(100% - 4px) !important',
      height: 'calc(100% - 4px) !important'
    },
    '&:hover': {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      background: `${mixins.tabHoverColor}`,
      backgroundColor: `${mixins.tabHoverColor}`
    }
  },
  // focus-visible treatment for a tab that is focused but NOT selected
  '&:not(.Mui-selected).Mui-focusVisible::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: 'none',
    width: 'calc(100% - 4px) !important',
    height: 'calc(100% - 4px) !important',
    borderRadius: '8px !important',
    border: `2px solid ${mixins.tabFocusVisibleColor}`
  }
};

const StyledTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'hasLeadingAction'
})(({ hasLeadingAction }) => ({
  '&&': {
    flex: 1,
    minHeight: 'unset',
    height: '100%',
    backgroundColor: 'transparent',
    margin: 0,
    padding: 0,
    '& .MuiTabs-indicator': {
      display: 'none'
    },
    '& .MuiTabs-flexContainer': {
      margin: 0,
      padding: '16px',
      gap: '8px',
      ...(hasLeadingAction ? { paddingLeft: '8px' } : {})
    },
    '& .MuiTab-root.MuiButtonBase-root': customTabRootStyles
  }
}));

const StyledTab = styled(Tab)({
  '&&&': customTabRootStyles
});

const a11yProps = index => ({
  id: `tab-${index}`,
  'aria-controls': `tab-panel-${index}`
});

const CustomTabs = ({
  selectedTab,
  tabList,
  children,
  onTabChange,
  className,
  leadingAction
}) => {
  const selectedIndex = Math.max(0, tabList.findIndex(tab => tab.id === selectedTab));
  const hasLeadingAction = Boolean(leadingAction);


  // eslint-disable-next-line no-shadow
  const renderTabs = (tabList) => {
    if (!tabList || tabList.length === 0) {
      return;
    }

    // eslint-disable-next-line consistent-return
    return (
      tabList.map((tab) => (
        <StyledTab
          key={tab.id}
          label={tab.label}
          disabled={tab.disabled}
          onClick={() => {
            if (tab.id === selectedTab) {
              onTabChange(tab.id);
            }
          }}
          {...a11yProps(tab.id)}
        />
      ))
    );
  };


  return (
    <TabsWrapper className={className}>
      <TabBarRow>
        {hasLeadingAction && (
          <LeadingAction>
            {leadingAction}
          </LeadingAction>
        )}
        <StyledTabs
          value={selectedIndex}
          onChange={(e, newIndex) => {
            const newTabId = tabList[newIndex].id;
            onTabChange(newTabId, e);
          }}
          hasLeadingAction={hasLeadingAction}
        >
          {renderTabs(tabList)}
        </StyledTabs>
      </TabBarRow>
      <TabPanel value={selectedIndex} index={selectedIndex}>
        {children(selectedTab)}
      </TabPanel>
    </TabsWrapper>
  );
};

CustomTabs.propTypes = {
  selectedTab: PropTypes.string,
  tabList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool
    })
  ).isRequired,
  children: PropTypes.node.isRequired,
  onTabChange: PropTypes.func,
  className: PropTypes.string,
  leadingAction: PropTypes.node
};

CustomTabs.defaultProps = {
  selectedTab: '',
  onTabChange: () => {},
  className: '',
  leadingAction: null
};

export default CustomTabs;
